"use client";

import { ArrowRight, ShieldCheck, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
export default function FuneralCostProtectionHero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const credentials = ["LICENSED INSURANCE AGENT", "PROUDLY SERVING FAMILIES", "COMMITMENT TO YOUR PEACE OF MIND", "NO MEDICAL EXAM INSURANCE SPECIALIST"];
  return <section data-section-id="238" id="funeral-cost-protection-hero" className="bg-[#061F3F] text-white relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(white 1px, transparent 1px)",
      backgroundSize: "3px 3px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-12 sm:px-8 lg:px-12 lg:pt-28 lg:pb-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-3 font-default text-[11px] uppercase tracking-[0.18em] text-[#FFCA2C]">
              <span className="inline-block h-2 w-2 rounded-lg" style={{
              backgroundColor: "#FFCA2C"
            }} aria-hidden="true" />
              <span>Final Expense Insurance</span>
              <span className="text-white/50">/</span>
              <span>Family Legacy Planning</span>
            </div>

            <h1 className="font-default mt-8 text-5xl font-bold leading-[1.02] text-white sm:text-6xl lg:text-7xl xl:text-8xl" style={{
            letterSpacing: "-0.03em"
          }}>
              Affordable Final Expense Insurance &{" "}
              <span className="relative inline-block">
                <span style={{
                color: "#FFCA2C"
              }}>Funeral Costs</span>
              </span>{" "}
              Burial Protection.
            </h1>

            <p className="font-default mt-8 max-w-[540px] text-xl leading-[1.65] text-white">
              Affordable no medical exam life insurance and burial insurance for seniors with fast approval options. Secure your legacy and ensure your loved ones are never burdened by unexpected costs.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Button asChild size="lg" className="h-12 rounded-md px-7 text-sm font-bold shadow-md hover:shadow-lg transition-shadow bg-[#F97316] text-white hover:bg-[#EA580C]">
                <Link to="/HomePage">
                  Get My Quote
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>

              <Link to="/HomePage" className="group inline-flex items-center gap-2 text-sm font-bold text-[#FFCA2C] hover:text-white transition-colors">
                <span className="border-b border-[#FFCA2C]/40 pb-0.5 group-hover:border-white">
                  Learn more about plans
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <Separator className="mt-12 bg-white/20" />

            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-default text-[11px] uppercase tracking-[0.15em] text-white">
              {credentials.map((cred, i) => <div key={cred} data-index={i} className="flex items-center gap-4">
                  {i > 0 && <span className="text-white/40">·</span>}
                  <span>{cred}</span>
                </div>)}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-5 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative">
              <FormProvider formId="quote_form_home" sectionName="hero-quote-section" className="relative z-10 rounded-lg border border-[#210 10% 90%] bg-white p-6 shadow-xl sm:p-8" defaultValues={{
              full_name: "",
              email_address: "",
              phone_number: "",
              date_of_birth: "",
              consent: false
            }} validationRules={{
              full_name: {
                required: true
              },
              email_address: {
                required: true,
                pattern: "email"
              },
              phone_number: {
                required: true
              },
              date_of_birth: {
                required: true
              },
              consent: {
                required: true
              }
            }}>
                <QuoteForm />
              </FormProvider>

              {}
              <div className="absolute -top-4 -right-4 h-24 w-24 rounded-lg bg-[#FFCA2C]/10 -z-10" />
              <div className="absolute -bottom-4 -left-4 h-32 w-32 rounded-lg bg-[#F97316]/10 -z-10" />
            </div>
          </div>
        </div>

        <Separator className="mt-24 bg-white/20" />
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-green-100 text-green-600">
          <ShieldCheck className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-bold text-[#061F3F]">Request Received</h3>
        <p className="mt-2 text-[#215 25% 40%]">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-[#061F3F]">Get Your Free Quote</h2>
          <p className="text-sm text-[#215 25% 40%] mt-1">Secure your family's future in minutes.</p>
        </div>

        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#061F3F] font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="border-[#210 10% 90%] focus:border-[#FFCA2C] text-[#061F3F]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField control={control} name="email_address" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#061F3F] font-semibold">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="border-[#210 10% 90%] focus:border-[#FFCA2C] text-[#061F3F]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="phone_number" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#061F3F] font-semibold">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(555) 000-0000" {...field} className="border-[#210 10% 90%] focus:border-[#FFCA2C] text-[#061F3F]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="date_of_birth" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#061F3F] font-semibold">Date of Birth</FormLabel>
              <FormControl>
                <Input type="date" {...field} className="border-[#210 10% 90%] focus:border-[#FFCA2C] text-[#061F3F]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consent" render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-[#210 10% 90%] p-4">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-[#061F3F] data-[state=checked]:bg-[#061F3F]" />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-xs text-[#215 25% 40%] font-normal">
                  I agree to the{" "}
                  <Link to="/PrivacyPolicyPage" className="text-[#061F3F] underline font-semibold">
                    Privacy Policy
                  </Link>{" "}
                  and consent to be contacted regarding insurance options.
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>} />

        {submitError && <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
            {submitError}
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold uppercase tracking-wider bg-[#F97316] text-white hover:bg-[#EA580C] shadow-md transition-all">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </> : "Get My Quote"}
        </Button>
        
        <p className="text-center text-[10px] text-[#215 25% 40%] uppercase tracking-widest">
          Est. Time: 60s • Secure & Encrypted
        </p>
      </div>
    </Form>;
}